import React, { useState } from 'react';
import { AttachmentForm } from './Attachments';
import { InboxOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { Button, Col, Divider, Form, Input, Row, Select, Typography, Upload } from 'components';
import { ValidationErrors } from 'constants/validations';
import { AddClient } from 'pages/admin/clients/addClient';
import { AddBrandModal } from 'pages/admin/brands/addBrand';
import { ClientSelect } from 'components/common/clientSelect';
import { useBrandSelectListQuery, useLeadSourceTypesQuery } from 'api';
import { ContactPersonAddEdit } from 'components/common/contactPerson/addEdit';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { useAdd } from './useAdd';
import moment from 'moment';
import { BrandsList } from './BrandsList';
import { BrandSelection } from './BrandSelection';
import styles from '../../styles.module.scss';

export const AddLead: React.FC = () => {
  const { hasAccess } = useAccessControl();
  const { data: sourceTypes = [] } = useLeadSourceTypesQuery();
  const { data: brandOptions = [] } = useBrandSelectListQuery();
  const [brandToAdd, setBrandToAdd] = useState<string | undefined>();
  const [openBrandModal, setOpenBrandModal] = useState(false);
  const [openClientModal, setOpenClientModal] = useState(false);
  const [openContactPersonModal, setOpenContactPersonModal] = useState(false);
  const {
    form,
    onFinish,
    onCancel,
    attachments,
    setAttachments,
    disableSaveBtn,
    setAttachmentType,
    fileList,
    setFileList,
    attachmentType,
    handleFormChange,
    createIsLoading,
    handleEmailUpload,
    emailPreview,
    brandList,
    handleDeleteBrand,
    onAddBrand,
    isLoadingContactPerson,
    contactPersons,
    selectedClient,
  } = useAdd();

  return (
    <Row justify={'end'} gutter={[0, 32]}>
      <Col span={24}>
        <Row gutter={[0, 24]} justify={'center'}>
          <Col span={24}>
            <Typography.Title className={styles.add_edit_title} level={2}>
              Add Lead
            </Typography.Title>
          </Col>
          <Col span={24} className={styles.form_container}>
            <Form
              onValuesChange={handleFormChange}
              form={form}
              requiredMark={false}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{ source: 'EMAIL' }}
            >
              <Row gutter={[32, 32]}>
                <Col span={12}>
                  <div className={styles.form_card}>
                    <Row gutter={[16, 16]}>
                      <Col span={24} className={'margin-t-20'}>
                        <Typography.Title level={5} style={{ color: '#05796B', margin: 0 }}>
                          Client Information
                        </Typography.Title>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          label="Client Name*"
                          name="clientId"
                          rules={[{ required: true, message: ValidationErrors.required('Client') }]}
                        >
                          <ClientSelect
                            size="large"
                            showAdd={hasAccess(PERMISSIONS.clients.client.write)}
                            onAdd={() => setOpenClientModal(true)}
                            onChange={() => {
                              form.setFieldsValue({ contactPersonId: undefined });
                            }}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          label="Contact Person*"
                          name="contactPersonId"
                          rules={[{ required: true, message: ValidationErrors.required('Contact Person') }]}
                        >
                          <Select
                            size={'large'}
                            loading={isLoadingContactPerson}
                            onAdd={() => setOpenContactPersonModal(true)}
                            showAdd={hasAccess(PERMISSIONS.clients.contact_person.write)}
                            disabled={!selectedClient}
                            options={hasAccess(PERMISSIONS.clients.contact_person.read) ? contactPersons : []}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          name="clientRequestNumber"
                          label="Client Request Number*"
                          rules={[{ required: true, message: ValidationErrors.required('Client Request Number') }]}
                        >
                          <Input size="large" />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          name="source"
                          label="Source*"
                          rules={[{ required: true, message: ValidationErrors.required('Source') }]}
                        >
                          <Select size="large" defaultValue={'EMAIL'} options={sourceTypes} />
                        </Form.Item>
                      </Col>

                      <Col span={24} className={'margin-t-20'}>
                        <Typography.Title level={5} style={{ color: '#05796B', margin: 0 }}>
                          Brands
                        </Typography.Title>
                      </Col>

                      <Col span={24}>
                        <BrandsList
                          brands={brandList.map((value) => ({
                            value,
                            label: brandOptions.find((opt: any) => opt.value === value)?.label || value,
                          }))}
                          onDelete={handleDeleteBrand}
                        />
                      </Col>

                      <Col span={24}>
                        <BrandSelection
                          form={form}
                          brandToAdd={brandToAdd}
                          setBrandToAdd={setBrandToAdd}
                          brandOptions={brandOptions}
                          brandList={brandList}
                          setOpenBrandModal={setOpenBrandModal}
                          onAddBrand={onAddBrand}
                        />
                      </Col>

                      <Col span={24} className={'margin-t-20'}>
                        <Typography.Title level={5} style={{ color: '#05796B', margin: 0 }}>
                          Attachments
                        </Typography.Title>
                      </Col>

                      <Col span={24}>
                        <AttachmentForm
                          attachments={attachments}
                          setAttachmentType={setAttachmentType}
                          fileList={fileList}
                          setAttachments={setAttachments}
                          setFileList={setFileList}
                          attachmentType={attachmentType}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>
                <Col span={12}>
                  <Row className={styles.form_card}>
                    <Col span={24}>
                      <Form.Item
                        name="emailFile"
                        rules={[
                          {
                            required: true,
                            message: ValidationErrors.required('Email'),
                          },
                        ]}
                        getValueFromEvent={(e) => e?.fileList}
                      >
                        <Upload.Dragger
                          accept=".eml"
                          className={'email-upload'}
                          beforeUpload={handleEmailUpload}
                          maxCount={1}
                          showUploadList={false}
                          style={{ marginBottom: '16px' }}
                        >
                          <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                          </p>
                          <p className="ant-upload-text">Click to Upload Email</p>
                          <p className="ant-upload-hint">Max size: 20MB / Format: EML</p>
                        </Upload.Dragger>
                      </Form.Item>
                    </Col>

                    {emailPreview && (
                      <Col className={'margin-t-20'} span={24}>
                        <Row gutter={[16, 16]} justify={'space-between'}>
                          <Col span={24} className={styles.original_email}>
                            Client Original Email
                          </Col>
                          <Col span={17}>
                            <Typography.Text>
                              <strong>Subject:</strong> <br />
                              {emailPreview.subject}
                            </Typography.Text>
                          </Col>
                          <Col span={7}>{moment(emailPreview.dateTime).format('DD.MM.YYYY HH:mm')}</Col>
                          <Col span={24}>
                            <strong>From:</strong>
                            <br />
                            {emailPreview.from}
                          </Col>
                          <Col className={'margin-t-20'} span={24}>
                            <Col className={'margin-t-20'} span={24}>
                              <iframe
                                srcDoc={emailPreview.body?.replace(/<a\s+(?![^>]*target=)/g, '<a target="_blank" ')}
                                className={'html-content-iframe'}
                                sandbox="allow-popups"
                              />
                            </Col>
                          </Col>
                        </Row>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Col>
      <Col offset={1} span={23}>
        <Divider style={{ marginBottom: 32 }} className={styles.divider} />
      </Col>
      <Col flex={'328px'}>
        <Row wrap={false} className="gap-8">
          <Button block size="large" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            block
            size="large"
            disabled={disableSaveBtn}
            type="primary"
            loading={createIsLoading}
            onClick={() => {
              form?.submit();
            }}
          >
            Add
          </Button>
        </Row>
      </Col>
      <AddBrandModal modalState={openBrandModal} onModalStateChange={() => setOpenBrandModal((os) => !os)} />
      <AddClient modalState={openClientModal} closeModal={() => setOpenClientModal((os) => !os)} />
      <ContactPersonAddEdit
        entityName={'CLIENT'}
        modalState={openContactPersonModal}
        clientId={selectedClient}
        onModalStateChange={() => setOpenContactPersonModal((oState) => !oState)}
      />
    </Row>
  );
};
